import { DEFAULT_OG_IMAGE, SITE_URL } from "./seoConfig";

function trimTrailingSlash(url) {
  return url.replace(/\/+$/, "");
}

export function buildPageUrl(path) {
  return path === "/" ? `${SITE_URL}/` : `${SITE_URL}${path}`;
}

function buildOrganizationSchema() {
  return {
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: "SeoBix",
    url: `${SITE_URL}/`,
    logo: `${SITE_URL}/GOSEO.svg`,
    telephone: "+917303484700",
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+917303484700",
      contactType: "customer support",
      areaServed: "IN",
      availableLanguage: ["en", "hi"],
    },
    sameAs: [
      "https://www.facebook.com/seobixofficial/",
      "https://www.instagram.com/seobixofficial/",
      "https://www.youtube.com/@Seobix",
    ],
  };
}

function buildWebsiteSchema() {
  return {
    "@type": "WebSite",
    "@id": `https://seobix.com/#website`,
    url: `${SITE_URL}/`,
    name: "SeoBix",
    description:
      "All-in-one SEO tools for website audits, keyword research, backlink analysis, rank tracking, and content optimization.",
    publisher: {
      "@id": `${SITE_URL}/#organization`,
    },
    inLanguage: "en-IN",
  };
}

function buildBreadcrumbSchema(page, url) {
  const items = [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: `${SITE_URL}/`,
    },
  ];

  if (page.path !== "/") {
    items.push({
      "@type": "ListItem",
      position: 2,
      name: page.breadcrumbName || page.title,
      item: url,
    });
  }

  return {
    "@type": "BreadcrumbList",
    "@id": `${trimTrailingSlash(url)}/#breadcrumb`,
    itemListElement: items,
  };
}

function buildWebPageSchema(page, url) {
  return {
    "@type": page.schemaType === "blog" ? "CollectionPage" : "WebPage",
    "@id": `${trimTrailingSlash(url)}/#webpage`,
    url,
    name: page.title,
    description: page.description,
    isPartOf: {
      "@id": `${SITE_URL}/#website`,
    },
    about: {
      "@id": `${SITE_URL}/#organization`,
    },
    breadcrumb: {
      "@id": `${trimTrailingSlash(url)}/#breadcrumb`,
    },
    primaryImageOfPage: {
      "@type": "ImageObject",
      url: page.image || DEFAULT_OG_IMAGE,
    },
    inLanguage: "en-IN",
  };
}

function buildToolSchema(page, url) {
  return {
    "@type": ["SoftwareApplication", "WebApplication"],
    "@id": `${trimTrailingSlash(url)}/#software`,
    name: page.breadcrumbName || page.title,
    url,
    applicationCategory: "BusinessApplication",
    applicationSubCategory: "SEO Tool",
    operatingSystem: "Web Browser",
    browserRequirements: "Requires JavaScript. Works on modern web browsers.",
    description: page.description,
    featureList: page.featureList,
    keywords: page.softwareKeywords || page.keywords,
    image: page.image || DEFAULT_OG_IMAGE,
    publisher: {
      "@id": `${SITE_URL}/#organization`,
    },
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      url,
    },
  };
}

function buildPricingSchema(page, url) {
  return {
    "@type": ["SoftwareApplication", "WebApplication"],
    "@id": `${trimTrailingSlash(url)}/#software`,
    name: "SeoBix",
    url: `${SITE_URL}/`,
    applicationCategory: "BusinessApplication",
    applicationSubCategory: "SEO Tools",
    description: page.description,
    publisher: {
      "@id": `${SITE_URL}/#organization`,
    },
    offers: [
      {
        "@type": "Offer",
        name: "Free Plan",
        price: "0",
        priceCurrency: "USD",
        url,
      },
      {
        "@type": "Offer",
        name: "Basic Plan",
        priceCurrency: "USD",
        url,
      },
      {
        "@type": "Offer",
        name: "Standard Plan",
        priceCurrency: "USD",
        url,
      },
      {
        "@type": "Offer",
        name: "Premium Plan",
        priceCurrency: "USD",
        url,
      },
    ],
  };
}

function buildFaqSchema(url) {
  return {
    "@type": "FAQPage",
    "@id": `${trimTrailingSlash(url)}/#faq`,
    mainEntity: [
      {
        "@type": "Question",
        name: "What is SeoBix?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "SeoBix is an SEO platform with tools for website audits, keyword research, backlink analysis, content optimization, and rank tracking.",
        },
      },
      {
        "@type": "Question",
        name: "Can I use SeoBix for multiple websites?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes, SeoBix can be used for multiple websites depending on your selected plan and project limits.",
        },
      },
      {
        "@type": "Question",
        name: "Does SeoBix offer a free trial?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "SeoBix offers options for users to explore SEO tools before choosing a paid plan.",
        },
      },
    ],
  };
}

function buildBlogSchema(page, url) {
  return {
    "@type": "Blog",
    "@id": `${trimTrailingSlash(url)}/#blog`,
    name: page.title,
    url,
    description: page.description,
    publisher: {
      "@id": `${SITE_URL}/#organization`,
    },
    inLanguage: "en-IN",
  };
}

export function buildJsonLd(page) {
  const url = buildPageUrl(page.path);
  const graph = [
    buildOrganizationSchema(),
    buildWebsiteSchema(),
    buildWebPageSchema(page, url),
    buildBreadcrumbSchema(page, url),
  ];

  if (page.schemaType === "tool") {
    graph.push(buildToolSchema(page, url));
  }

  if (page.schemaType === "pricing") {
    graph.push(buildPricingSchema(page, url));
  }

  if (page.schemaType === "faq") {
    graph.push(buildFaqSchema(url));
  }

  if (page.schemaType === "blog") {
    graph.push(buildBlogSchema(page, url));
  }

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
}
